import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { useThreeRenderer } from './rendering/ThreeRenderer';
import DownloadButton from './buttons/DownloadButton';
import GridButton from './buttons/GridButton';
import CameraPositionLogger from './buttons/CameraPositionLogger';
import CameraPositionSetter from './buttons/CameraPositionSetter';
import AxisLegend from './helpers/AxisLegend';

// showToolbar: キャンバス下の開発・調整用ボタン（DL／グリッド／カメラ位置）を出すか。
// 既定は true（従来どおり）。実験実施時は false にして学習者から隠す想定で、
// false のときはツールバーごと描かないのでキャンバスの下に余白も残らない。
function ModelViewer({ glbPath, imagePath, caption, canvasId, shearValue, addColor, cameraPosition, cameraTarget, cameraTiltAngle = 0, externalNewCameraPosition, externalNewCameraTarget, isOrbitControlsEnabled = true, persistentEyePosition = null, persistentEyeFrom = null, showToolbar = true, axisLegendPlacement = 'top-left' }, ref) {
  const isModelVisible = true;
  const [isGridVisible, setIsGridVisible] = useState(true);
  const [newCameraPosition, setNewCameraPosition] = useState(cameraPosition);
  const [newCameraTarget, setNewCameraTarget] = useState(cameraTarget);
  const canvasRef = useRef(null);
  const {
    renderer,
    scene,
    camera,
    isRendererReady,
    animateCameraTo,
    snapCameraTo,
    playEyeAnimation,
    clearEye,
    setOrbitEnabled,
  } = useThreeRenderer(
    glbPath,
    canvasRef,
    isModelVisible,
    isGridVisible,
    isOrbitControlsEnabled,
    shearValue,
    addColor,
    newCameraPosition,
    newCameraTarget,
    cameraTiltAngle,
    persistentEyePosition,
    persistentEyeFrom
  );

  // 親コンポーネントが現在のカメラ位置を直接取得・命令的アニメーションを実行できるようにする
  useImperativeHandle(ref, () => ({
    getCameraPosition: () => {
      try {
        if (camera && camera.position) {
          return { x: camera.position.x, y: camera.position.y, z: camera.position.z };
        }
      } catch (e) {
        console.warn('getCameraPosition failed', e);
      }
      return null;
    },
    animateCameraTo: (pos, duration, onDone) => animateCameraTo && animateCameraTo(pos, duration, onDone),
    snapCameraTo: (pos) => snapCameraTo && snapCameraTo(pos),
    playEyeAnimation: (from, to, duration) => playEyeAnimation && playEyeAnimation(from, to, duration),
    clearEye: () => clearEye && clearEye(),
    setOrbitEnabled: (enabled) => setOrbitEnabled && setOrbitEnabled(enabled),
  }), [camera, animateCameraTo, snapCameraTo, playEyeAnimation, clearEye, setOrbitEnabled]);

  useEffect(() => {
    if (!isModelVisible && renderer) {
      // 3Dモデルが表示されていない時、レンダラーを停止してメモリリークを防ぐ
      console.log("dispose");
      renderer.dispose();
    }
  }, [isModelVisible, renderer]);

  useEffect(() => {
    if (renderer && scene && camera && isRendererReady) {
      const canvas = canvasRef.current;
      if (canvas) {
        renderer.setSize(canvas.clientWidth, canvas.clientHeight);
        renderer.render(scene, camera);
      } else {
        console.error("Canvas element is not initialized.");
      }
    }
  }, [renderer, scene, camera, isRendererReady]);

  // 外部から渡されたカメラ位置が変わったら内部 state に反映し、存在する camera に即時適用する
  useEffect(() => {
    if (!externalNewCameraPosition) return;
    setNewCameraPosition(externalNewCameraPosition);
  }, [externalNewCameraPosition, camera]);

  useEffect(() => {
    if (!externalNewCameraTarget) return;
    setNewCameraTarget(externalNewCameraTarget);
  }, [externalNewCameraTarget, camera]);

  useEffect(() => {
    if (cameraTarget) {
      setNewCameraTarget(cameraTarget);
    }
  }, [cameraTarget]);

  return (
    <div className="relative">
      <AxisLegend isVisible={isGridVisible && isRendererReady} placement={axisLegendPlacement} />
        {isModelVisible ? (
        <canvas id={canvasId} ref={canvasRef} className="model-canvas w-full h-64"></canvas>
      ) : (
        <img src={imagePath} alt={`${caption}のデッサン`} className="w-full h-96 object-contain" />
      )}

      {/* ボタンをフレックスボックスで水平に並べる（showToolbar=false なら枠ごと出さない） */}
      {showToolbar && (
        <div className="modelviewer-toolbar">
          {/* モデルが表示されている時だけ機能ボタンを表示 */}
          {isModelVisible && isRendererReady && (
            <>
              <DownloadButton renderer={renderer} scene={scene} camera={camera} glbPath={glbPath} />
              <GridButton
                isGridVisible={isGridVisible}
                setIsGridVisible={setIsGridVisible} />
              <CameraPositionLogger camera={camera} />
              <CameraPositionSetter
                camera={camera}
                newCameraPosition={newCameraPosition}
                setNewCameraPosition={setNewCameraPosition}
                defaultPosition={{ x: 0, y: 194, z: -461 }}
                canvasId={canvasId}
                glbPath={glbPath}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default React.forwardRef(ModelViewer);